// Angular
import { Component } from '@angular/core';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';

// PrimeNG
import { AutoFocusModule } from 'primeng/autofocus';
import { ButtonModule } from 'primeng/button';
import { InputOtpModule } from 'primeng/inputotp';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

// QR Code
import * as QRCode from 'qrcode';

// Composants
import { FormBaseComponent } from 'src/app/@shared/components/abstract/form-base.component';

// Modèles
import { QrLoginTicket } from 'src/app/@core/authentication/services/qr-login.service';

type QrLoginStep = 'totp' | 'loading' | 'qrcode' | 'expired';

@Component({
    selector: 'app-qr-login',
    imports: [
        ReactiveFormsModule,
        AutoFocusModule,
        ButtonModule,
        InputOtpModule,
        ProgressSpinnerModule,
    ],
    templateUrl: './qr-login.component.html',
    styleUrls: ['./qr-login.component.scss']
})
export class QrLoginComponent extends FormBaseComponent {

    /*----------------------------------------------------------------------------------------
     * Gestion de la vue
     ----------------------------------------------------------------------------------------*/

    public step: QrLoginStep = 'loading';
    public qrCodeDataUrl: string = '';
    public countdown: number = 60;

    /*----------------------------------------------------------------------------------------
     * Données
     ----------------------------------------------------------------------------------------*/

    private _expiresIn: number = 60;
    private _timer: ReturnType<typeof setInterval> | null = null;

    /*----------------------------------------------------------------------------------------
     * Méthodes publiques
     ----------------------------------------------------------------------------------------*/

    public override ngOnDestroy(): void {
        this._clearTimer();
        super.ngOnDestroy();
    }

    public onClickRetry(): void {
        this._retry();
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes protégées
     ----------------------------------------------------------------------------------------*/

    protected override buildForm() {
        this.form = this.formBuilder.group({
            code: ['', [Validators.required, Validators.minLength(6), Validators.maxLength(6)]]
        });
    }

    override ngOnInit() {
        super.ngOnInit();
        if (this.appService.user?.mfa) {
            this.step = 'totp';
        } else {
            this._generate();
        }
    }

    protected override subscribeToObservables(): void {
        this.subscriptions.add(
            this.form.get('code')?.valueChanges.subscribe(() => {
                if (this.form.get('code')?.valid) {
                    this._generate(this.form.get('code')?.value as string);
                }
            })
        );
    }

    /*----------------------------------------------------------------------------------------
     * Méthodes privées
     ----------------------------------------------------------------------------------------*/

    private _clearTimer(): void {
        if (this._timer) {
            clearInterval(this._timer);
            this._timer = null;
        }
    }

    private _generate(totpCode?: string): void {
        this.step = 'loading';
        this._setLoading(true);
        this.qrLoginService.generateTicket$(totpCode)
            .pipe(finalize(() => {
                this._setLoading(false);
            }))
            .subscribe({
                next: ({ ticket, expiresIn }: QrLoginTicket) => {
                    this._expiresIn = expiresIn;
                    QRCode.toDataURL(ticket, { width: 220, margin: 2 }).then(url => {
                        this.qrCodeDataUrl = url;
                        this.step = 'qrcode';
                        this._startCountdown(expiresIn);
                    });
                },
                error: (err: any) => {
                    this.messageService.sendODataMessages(err.error?.messages);
                    this.step = this.appService.user?.mfa ? 'totp' : 'expired';
                    this.form.get('code')?.reset();
                }
            });
    }

    private _retry(): void {
        if (this.appService.user?.mfa) {
            this.form.get('code')?.reset();
            this.step = 'totp';
        } else {
            this._generate();
        }
    }

    private _setLoading(loading: boolean): void {
        this.loading = loading;
        if (loading) {
            this.form.disable({ emitEvent: false });
        } else {
            this.form.enable({ emitEvent: false });
        }
    }

    private _startCountdown(seconds: number): void {
        this._clearTimer();
        this.countdown = seconds;
        this._timer = setInterval(() => {
            this.countdown--;
            if (this.countdown <= 0) {
                this._clearTimer();
                this.step = 'expired';
                this.qrCodeDataUrl = '';
            }
        }, 1000);
    }
}
